import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, List, ListItemButton,
  ListItemText, Stack, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon, Link as LinkIcon } from '@mui/icons-material';
import axios from 'axios';
import useSWR from 'swr';
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });

const pesanGalat = (e, bawaan) => e.response?.data?.message || e.response?.data?.msg || bawaan;

/** Status fitur: bolehAtur = pengguna ini superadmin (hanya superadmin yang melihat dan mengubah tautan ulasan); pencarianOtomatis = kunci Google Places terpasang. */
export const useStatusUlasan = () => {
  const { data, isLoading } = useSWR('/ulasan-google/status', (url) => api.get(url).then((r) => r.data).catch(() => null), { revalidateOnFocus: false });
  return { memuat: !!isLoading, bolehAtur: data?.data?.bolehAtur === true, pencarianOtomatis: !!data?.data?.pencarianOtomatis };
};

/** Apakah pencarian otomatis (Google Places) siap: kunci terpasang di Integrasi oleh superadmin. */
export const usePencarianOtomatis = () => useStatusUlasan().pencarianOtomatis;

/** Dialog: tempel tautan Google Maps cabang (tombol Bagikan) -> tautan ulasan. */
export const DialogTautanMaps = ({ open, cabangUuid, onClose, onPilih }) => {
  const [tautan, setTautan] = useState('');
  const [memeriksa, setMemeriksa] = useState(false);
  const [hasil, setHasil] = useState(null);
  const [galat, setGalat] = useState('');

  useEffect(() => { if (open) { setTautan(''); setHasil(null); setGalat(''); } }, [open]);

  const periksa = async () => {
    setMemeriksa(true);
    setGalat('');
    setHasil(null);
    try {
      const r = await api.post('/ulasan-google/dari-tautan', { cabangUuid, tautan });
      setHasil(r.data.data);
    } catch (e) {
      setGalat(pesanGalat(e, 'Tautan tidak dapat diperiksa.'));
    } finally {
      setMemeriksa(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ambil dari tautan Google Maps</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Buka cabang di Google Maps, tekan <b>Bagikan</b>, salin tautannya, lalu tempel di sini.
          Tautan pendek (maps.app.goo.gl) dan tautan panjang sama-sama bisa.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Tautan Google Maps cabang"
          placeholder="https://maps.app.goo.gl/..."
          value={tautan}
          onChange={(e) => { setTautan(e.target.value); setHasil(null); setGalat(''); }}
          disabled={memeriksa}
          autoFocus
        />
        {galat && <Alert severity="error" sx={{ mt: 1.5 }}>{galat}</Alert>}
        {hasil && (
          <Box sx={{ mt: 1.5 }} data-testid="hasil-tautan">
            <Alert severity={hasil.peringatan ? 'warning' : 'success'}>
              <b>{hasil.nama || 'Tempat ditemukan'}</b>
              {hasil.jarakMeter !== null && hasil.jarakMeter !== undefined && !hasil.peringatan && ` · sekitar ${hasil.jarakMeter} m dari titik cabang`}
              {hasil.peringatan && <><br />{hasil.peringatan}</>}
            </Alert>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
              {hasil.tautanUlasan}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button onClick={periksa} disabled={memeriksa || !tautan.trim()} variant={hasil ? 'text' : 'contained'}>
          {memeriksa ? 'Memeriksa...' : 'Periksa tautan'}
        </Button>
        {hasil && <Button variant="contained" onClick={() => { onPilih(hasil.tautanUlasan); onClose(); }}>Pakai tautan ini</Button>}
      </DialogActions>
    </Dialog>
  );
};


/**
 * Dialog "Isi dari tautan Google Maps" untuk form Cabang: dari satu tautan Maps diusulkan nama, alamat (bila kunci Google Places
 * terpasang), koordinat, dan tautan ulasan. Pengguna mencentang yang dipakai; nama boleh diganti sendiri. Tidak ada yang ditimpa
 * tanpa dicentang. onTerapkan({ nama?, alamat?, koordinat?, tautanUlasan? }).
 */
export const DialogIsiCabangDariMaps = ({ open, cabangUuid, formSekarang, onClose, onTerapkan }) => {
  const [tautan, setTautan] = useState('');
  const [memeriksa, setMemeriksa] = useState(false);
  const [hasil, setHasil] = useState(null);
  const [galat, setGalat] = useState('');
  const [pilih, setPilih] = useState({ nama: false, alamat: false, koordinat: false, ulasan: true });
  const [nama, setNama] = useState('');
  const [alamat, setAlamat] = useState('');

  const urutan = useRef(0); // respons permintaan lama (dialog ditutup, tautan diganti) diabaikan

  useEffect(() => { urutan.current += 1; if (open) { setTautan(''); setHasil(null); setGalat(''); setMemeriksa(false); } }, [open]);

  const periksa = async () => {
    const id = (urutan.current += 1);
    setMemeriksa(true);
    setGalat('');
    setHasil(null);
    try {
      const r = await api.post('/ulasan-google/dari-tautan', { ...(cabangUuid ? { cabangUuid } : {}), tautan });
      if (id !== urutan.current) return;
      const d = r.data.data;
      setHasil(d);
      setNama(d.nama || '');
      setAlamat(d.alamat || '');
      // Yang sudah terisi di form tidak dicentang (tidak menimpa tanpa disengaja); yang masih kosong dicentang.
      setPilih({
        nama: !String(formSekarang?.namacabang || '').trim() && !!d.nama,
        alamat: !String(formSekarang?.alamat || '').trim() && !!d.alamat,
        koordinat: !String(formSekarang?.koordinat || '').trim() && !!d.koordinat,
        ulasan: !!d.tautanUlasan, // tautan ulasan hanya dikirim ke superadmin
      });
    } catch (e) {
      if (id === urutan.current) setGalat(pesanGalat(e, 'Tautan tidak dapat diperiksa.'));
    } finally {
      if (id === urutan.current) setMemeriksa(false);
    }
  };

  const terapkan = () => {
    onTerapkan({
      ...(pilih.nama && nama.trim() ? { nama: nama.trim() } : {}),
      ...(pilih.alamat && alamat.trim() ? { alamat: alamat.trim() } : {}),
      ...(pilih.koordinat && hasil.koordinat ? { koordinat: hasil.koordinat } : {}),
      ...(pilih.ulasan && hasil.tautanUlasan ? { tautanUlasan: hasil.tautanUlasan } : {}),
    });
    onClose();
  };
  const adaPilihan = hasil && (pilih.nama || pilih.alamat || pilih.koordinat || (pilih.ulasan && hasil.tautanUlasan));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Isi dari tautan Google Maps</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Buka cabang di Google Maps, tekan <b>Bagikan</b>, salin tautannya, lalu tempel di sini. Centang data yang mau dipakai di form cabang.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Tautan Google Maps cabang"
          placeholder="https://maps.app.goo.gl/..."
          value={tautan}
          onChange={(e) => { setTautan(e.target.value); setHasil(null); setGalat(''); }}
          disabled={memeriksa}
          autoFocus
        />
        {galat && <Alert severity="error" sx={{ mt: 1.5 }}>{galat}</Alert>}
        {hasil && (
          <Box sx={{ mt: 1.5 }} data-testid="usulan-cabang">
            {hasil.peringatan && <Alert severity="warning" sx={{ mb: 1 }}>{hasil.peringatan}</Alert>}
            <FormControlLabel
              control={<Checkbox checked={pilih.nama} onChange={(e) => setPilih((p) => ({ ...p, nama: e.target.checked }))} />}
              label="Isi nama cabang"
            />
            <TextField fullWidth size="small" label="Nama cabang (usulan dari Google Maps, boleh diganti)" value={nama} onChange={(e) => setNama(e.target.value)} disabled={!pilih.nama} sx={{ mb: 1 }} />
            {hasil.alamat ? (
              <>
                <FormControlLabel
                  control={<Checkbox checked={pilih.alamat} onChange={(e) => setPilih((p) => ({ ...p, alamat: e.target.checked }))} />}
                  label="Isi alamat"
                />
                <TextField fullWidth size="small" label="Alamat (dari Google Maps, boleh diubah)" value={alamat} onChange={(e) => setAlamat(e.target.value)} disabled={!pilih.alamat} sx={{ mb: 1 }} />
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Alamat tidak ada di tautan Maps; isi manual (atau minta superadmin memasang kunci Google Places di menu Integrasi).
              </Typography>
            )}
            {hasil.koordinat ? (
              <FormControlLabel
                control={<Checkbox checked={pilih.koordinat} onChange={(e) => setPilih((p) => ({ ...p, koordinat: e.target.checked }))} />}
                label={`Isi koordinat cabang: ${hasil.koordinat}`}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" display="block">Tautan ini tidak memuat koordinat.</Typography>
            )}
            {hasil.tautanUlasan && (
              <FormControlLabel
                control={<Checkbox checked={pilih.ulasan} onChange={(e) => setPilih((p) => ({ ...p, ulasan: e.target.checked }))} />}
                label="Pakai sebagai tautan ulasan Google (QR struk, layar pelanggan, menu QR, kartu)"
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button onClick={periksa} disabled={memeriksa || !tautan.trim()} variant={hasil ? 'text' : 'contained'}>
          {memeriksa ? 'Memeriksa...' : 'Periksa tautan'}
        </Button>
        {hasil && <Button variant="contained" onClick={terapkan} disabled={!adaPilihan}>Terapkan ke form</Button>}
      </DialogActions>
    </Dialog>
  );
};

/** Dialog: cari otomatis di Google Places dari nama, alamat, dan titik cabang; pengguna memilih kandidat yang benar. */
export const DialogCariUlasan = ({ open, cabangUuid, namaCabang, onClose, onPilih }) => {
  const [memuat, setMemuat] = useState(false);
  const [kandidat, setKandidat] = useState(null);
  const [galat, setGalat] = useState('');
  const [terpilih, setTerpilih] = useState('');

  useEffect(() => {
    if (!open || !cabangUuid) return undefined;
    let batal = false;
    setMemuat(true);
    setGalat('');
    setKandidat(null);
    setTerpilih('');
    api.post('/ulasan-google/cari', { cabangUuid })
      .then((r) => {
        if (batal) return;
        const daftar = r.data.data.kandidat || [];
        setKandidat(daftar);
        const dekat = daftar.filter((k) => k.dekat);
        if (dekat.length === 1) setTerpilih(dekat[0].placeId); // satu-satunya yang dekat: dipilihkan, tetap diperiksa pengguna
      })
      .catch((e) => { if (!batal) setGalat(pesanGalat(e, 'Pencarian gagal.')); })
      .finally(() => { if (!batal) setMemuat(false); });
    return () => { batal = true; };
  }, [open, cabangUuid]);

  const pilihan = (kandidat || []).find((k) => k.placeId === terpilih);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cari otomatis di Google{namaCabang ? `: ${namaCabang}` : ''}</DialogTitle>
      <DialogContent>
        {memuat && <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}><CircularProgress size={22} /><Typography>Mencari tempat usaha...</Typography></Stack>}
        {galat && <Alert severity="error">{galat}</Alert>}
        {kandidat && kandidat.length === 0 && (
          <Alert severity="info">
            Tidak ada tempat yang cocok di sekitar titik cabang. Mungkin cabang ini belum terdaftar di Google Maps. Daftarkan dulu di Profil Bisnis Google,
            atau pakai &quot;Ambil dari tautan Google Maps&quot;.
          </Alert>
        )}
        {kandidat && kandidat.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Pilih tempat yang benar. Periksa nama dan alamatnya sebelum memakai.</Typography>
            <List dense disablePadding>
              {kandidat.map((k) => (
                <ListItemButton key={k.placeId} selected={terpilih === k.placeId} onClick={() => setTerpilih(k.placeId)} sx={{ borderRadius: 1, mb: 0.5, border: '1px solid', borderColor: 'divider' }}>
                  <ListItemText
                    primary={<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"><b>{k.nama}</b>{k.dekat && <Chip size="small" color="success" label="Dekat titik cabang" />}</Stack>}
                    secondary={`${k.alamat}${k.rating !== null ? ` · ★ ${k.rating} (${k.jumlahUlasan} ulasan)` : ''}${k.jarakMeter !== null ? ` · ${k.jarakMeter} m dari titik cabang` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button variant="contained" disabled={!pilihan} onClick={() => { onPilih(pilihan.tautanUlasan); onClose(); }}>Pakai tempat ini</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Dua tombol pengisi tautan ulasan (dari tautan Google Maps; cari otomatis bila kunci terpasang) beserta dialognya. */
export const TombolUlasanOtomatis = ({ cabangUuid, namaCabang, onPilih, disabled }) => {
  const otomatis = usePencarianOtomatis();
  const [dialog, setDialog] = useState('');
  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => setDialog('tautan')} disabled={disabled || !cabangUuid}>
          Ambil dari tautan Google Maps
        </Button>
        {otomatis && (
          <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => setDialog('cari')} disabled={disabled || !cabangUuid}>
            Cari otomatis
          </Button>
        )}
      </Stack>
      <DialogTautanMaps open={dialog === 'tautan'} cabangUuid={cabangUuid} onClose={() => setDialog('')} onPilih={onPilih} />
      <DialogCariUlasan open={dialog === 'cari'} cabangUuid={cabangUuid} namaCabang={namaCabang} onClose={() => setDialog('')} onPilih={onPilih} />
    </>
  );
};
